/**
 * FairnessReport.js - Collapsible fairness analysis display for draft schedules
 *
 * Shows fairness grade, summary, structured explanations with visual indicators,
 * time slot distribution, and matchup matrix.
 * Uses inline styles per project convention.
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

const gradeColors = {
  A: { bg: '#d4edda', color: '#155724', border: '#28a745' },
  B: { bg: '#fff3cd', color: '#856404', border: '#ffc107' },
  C: { bg: '#f8d7da', color: '#721c24', border: '#dc3545' }
};

const gradeColorsDark = {
  A: { bg: '#1a3a1a', color: '#7ddf7d', border: '#28a745' },
  B: { bg: '#3a3000', color: '#ffe066', border: '#ffc107' },
  C: { bg: '#3a1a1a', color: '#ff8a8a', border: '#dc3545' }
};

const severityConfig = {
  pass: { icon: '\u2705', label: 'Passing', iconColor: '#28a745', bg: '#d4edda', darkBg: '#1a3a1a', darkColor: '#7ddf7d' },
  info: { icon: '\u2139\uFE0F', label: 'Inherent', iconColor: '#6c757d', bg: '#e9ecef', darkBg: '#2a2a2a', darkColor: '#adb5bd' },
  warning: { icon: '\u26A0\uFE0F', label: 'Actionable', iconColor: '#ffc107', bg: '#fff3cd', darkBg: '#3a3000', darkColor: '#ffe066' },
  concern: { icon: '\u26A0\uFE0F', label: 'Significant', iconColor: '#dc3545', bg: '#f8d7da', darkBg: '#3a1a1a', darkColor: '#ff8a8a' }
};

const categoryLabels = {
  timeSlotDistribution: 'Time Slot Balance',
  opponentVariety: 'Matchup Balance',
  homeAway: 'Home/Away Balance',
  restDays: 'Rest Day Balance'
};

function FairnessReport({ leagueId }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fairness, setFairness] = useState(null);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const isDark = typeof window !== 'undefined' &&
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  const loadFairnessData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.getLeague(leagueId);
      const fa = response.league.scheduleData?.fairnessAnalysis;
      if (fa) {
        setFairness(fa);
      } else {
        setError('No fairness data available for this league.');
      }
    } catch (err) {
      setError('Failed to load fairness data.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (expanded && !fairness && !loading) {
      loadFairnessData();
    }
  }, [expanded, fairness, loading, loadFairnessData]);

  const gc = fairness
    ? ((isDark ? gradeColorsDark : gradeColors)[fairness.fairnessGrade] || gradeColors.C)
    : null;

  // Get sorted time slots from distribution data
  const getTimeSlots = () => {
    if (!fairness?.timeSlotDistribution) return [];
    const teams = Object.keys(fairness.timeSlotDistribution);
    if (teams.length === 0) return [];
    const slots = Object.keys(fairness.timeSlotDistribution[teams[0]]);
    return slots.sort((a, b) => parseTime(a) - parseTime(b));
  };

  // Use structured explanations if available, fall back to legacy strings
  const hasStructured = fairness?.explanations && fairness.explanations.length > 0;
  const displaySummary = fairness?.gradeSummary || fairness?.summary || '';

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none',
          border: '1px solid var(--color-border, #ddd)',
          borderRadius: 6,
          padding: '6px 12px',
          cursor: 'pointer',
          fontSize: '0.82rem',
          color: 'var(--color-primary, #667eea)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          justifyContent: 'space-between'
        }}
      >
        <span>Fairness Report</span>
        <span style={{
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          fontSize: '0.7rem'
        }}>&#9660;</span>
      </button>

      {expanded && (
        <div style={{
          marginTop: 8,
          padding: 12,
          background: 'var(--color-surface, #f9f9ff)',
          borderRadius: 8,
          border: '1px solid var(--color-border, #eee)',
          fontSize: '0.85rem'
        }}>
          {loading && (
            <div style={{ color: 'var(--color-text-muted, #888)', textAlign: 'center', padding: 16 }}>Loading...</div>
          )}

          {error && (
            <div style={{ color: '#e74c3c', fontSize: '0.82rem' }}>{error}</div>
          )}

          {fairness && (
            <>
              {/* Grade badge + summary */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div
                  style={{
                    position: 'relative',
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: gc.bg,
                    border: `2px solid ${gc.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    color: gc.color,
                    flexShrink: 0,
                    cursor: 'default'
                  }}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  {fairness.fairnessGrade}
                  {showTooltip && displaySummary && (
                    <div style={{
                      position: 'absolute',
                      bottom: '110%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'var(--color-bg-tooltip, #333)',
                      color: 'var(--color-text-tooltip, #fff)',
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                      whiteSpace: 'normal',
                      width: 220,
                      textAlign: 'center',
                      zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      fontWeight: 400,
                      pointerEvents: 'none'
                    }}>
                      {displaySummary}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{
                    margin: 0,
                    color: 'var(--color-text, #444)',
                    lineHeight: 1.4,
                    fontSize: '0.82rem',
                    fontWeight: 600
                  }}>
                    {displaySummary}
                  </p>
                  <p style={{
                    margin: '2px 0 0',
                    color: 'var(--color-text-muted, #777)',
                    lineHeight: 1.4,
                    fontSize: '0.78rem'
                  }}>
                    {fairness.summary}
                  </p>
                </div>
              </div>

              {/* Structured explanations */}
              {hasStructured && (
                <div style={{ marginBottom: 12 }}>
                  {fairness.explanations.map((item, i) => {
                    const sev = severityConfig[item.severity] || severityConfig.info;
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          padding: '6px 10px',
                          marginBottom: 4,
                          background: isDark ? sev.darkBg : sev.bg,
                          borderRadius: 6,
                          borderLeft: `3px solid ${sev.iconColor}`,
                          fontSize: '0.8rem',
                          lineHeight: 1.5,
                          color: isDark ? sev.darkColor : 'var(--color-text, #444)'
                        }}
                      >
                        <span style={{ flexShrink: 0, fontSize: '0.9rem' }}>{sev.icon}</span>
                        <div style={{ flex: 1 }}>
                          <span style={{
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                            color: isDark ? sev.darkColor : sev.iconColor,
                            marginRight: 6
                          }}>
                            {categoryLabels[item.category] || item.category}
                          </span>
                          <span>{item.message}</span>
                        </div>
                      </div>
                    );
                  })}

                  {fairness.canRegenerate && (
                    <p style={{
                      margin: '8px 0 0',
                      padding: '6px 10px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: gc.color,
                      background: gc.bg,
                      borderRadius: 6,
                      borderLeft: `3px solid ${gc.border}`
                    }}>
                      Tip: Try re-generating your schedule for a potentially better result.
                    </p>
                  )}
                </div>
              )}

              {/* Legacy explanation fallback for older cached data */}
              {!hasStructured && fairness.fairnessGrade !== 'A' && fairness.explanation && fairness.explanation.length > 0 && (
                <div style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  background: gc.bg,
                  border: `1px solid ${gc.border}33`,
                  borderLeft: `3px solid ${gc.border}`,
                  borderRadius: 6,
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                  color: 'var(--color-text, #444)'
                }}>
                  {fairness.explanation.map((line, i) => (
                    <p key={i} style={{ margin: i === 0 ? 0 : '6px 0 0' }}>{line}</p>
                  ))}
                  {fairness.canRegenerate && (
                    <p style={{
                      margin: '8px 0 0',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: gc.color
                    }}>
                      Tip: Try re-generating your schedule for a potentially better result.
                    </p>
                  )}
                </div>
              )}

              {/* Time Slot Distribution */}
              <TimeSlotTable
                distribution={fairness.timeSlotDistribution}
                earlyLateBalance={fairness.earlyLateBalance}
                timeSlots={getTimeSlots()}
              />

              {/* Matchup Matrix */}
              <MatchupMatrix matrix={fairness.matchupMatrix} />

              {/* Disclaimer */}
              <p style={{
                margin: '12px 0 0',
                fontSize: '0.75rem',
                color: 'var(--color-text-muted, #999)',
                fontStyle: 'italic',
                lineHeight: 1.4
              }}>
                This report is for your review only. It will not be visible on the public page or in published PDFs.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Time Slot Distribution table
 */
function TimeSlotTable({ distribution, earlyLateBalance, timeSlots }) {
  const teams = Object.keys(distribution);
  if (teams.length === 0 || timeSlots.length === 0) return null;

  const earliestSlot = timeSlots[0];
  const latestSlot = timeSlots[timeSlots.length - 1];

  const thStyle = {
    padding: '4px 8px',
    textAlign: 'center',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: 'var(--color-text-muted, #555)',
    borderBottom: '2px solid var(--color-border, #ddd)',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '4px 8px',
    textAlign: 'center',
    fontSize: '0.78rem',
    color: 'var(--color-text, #333)',
    borderBottom: '1px solid var(--color-border, #eee)'
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <h4 style={{ margin: '0 0 6px', fontSize: '0.8rem', color: 'var(--color-text-muted, #555)', fontWeight: 600 }}>
        Time Slot Distribution
      </h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>Team</th>
              {timeSlots.map(slot => (
                <th
                  key={slot}
                  style={{
                    ...thStyle,
                    background: slot === earliestSlot || slot === latestSlot ? 'var(--color-highlight, #f0f0ff)' : 'transparent'
                  }}
                >
                  {slot}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teams.map(team => (
              <tr key={team}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {team}
                </td>
                {timeSlots.map(slot => (
                  <td
                    key={slot}
                    style={{
                      ...tdStyle,
                      background: slot === earliestSlot || slot === latestSlot ? 'var(--color-highlight, #f0f0ff)' : 'transparent',
                      fontWeight: slot === earliestSlot || slot === latestSlot ? 600 : 400
                    }}
                  >
                    {distribution[team][slot] || 0}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Matchup Matrix grid per group
 */
function MatchupMatrix({ matrix }) {
  const groupKeys = Object.keys(matrix);
  if (groupKeys.length === 0) return null;

  const thStyle = {
    padding: '4px 6px',
    textAlign: 'center',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: 'var(--color-text-muted, #555)',
    borderBottom: '2px solid var(--color-border, #ddd)',
    whiteSpace: 'nowrap',
    maxWidth: 80,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const tdStyle = {
    padding: '4px 6px',
    textAlign: 'center',
    fontSize: '0.78rem',
    color: 'var(--color-text, #333)',
    borderBottom: '1px solid var(--color-border, #eee)'
  };

  return (
    <div>
      {groupKeys.map(groupKey => {
        const groupMatrix = matrix[groupKey];
        const teams = Object.keys(groupMatrix);
        if (teams.length === 0) return null;

        const label = groupKeys.length > 1
          ? `Matchup Matrix — ${groupKey.replace('group', 'Group ')}`
          : 'Matchup Matrix';

        return (
          <div key={groupKey} style={{ marginBottom: 8 }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '0.8rem', color: 'var(--color-text-muted, #555)', fontWeight: 600 }}>
              {label}
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: 'left' }}>vs</th>
                    {teams.map(t => (
                      <th key={t} style={thStyle} title={t}>
                        {t.length > 8 ? t.slice(0, 7) + '\u2026' : t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teams.map(team => (
                    <tr key={team}>
                      <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {team}
                      </td>
                      {teams.map(opp => (
                        <td
                          key={opp}
                          style={{
                            ...tdStyle,
                            background: team === opp ? 'var(--color-bg-muted, #f0f0f0)' : 'transparent',
                            color: team === opp ? 'var(--color-text-disabled, #ccc)' : 'var(--color-text, #333)'
                          }}
                        >
                          {team === opp ? '\u2014' : (groupMatrix[team][opp] || 0)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Parse time string to minutes for sorting (mirrors server logic)
 */
function parseTime(timeStr) {
  const normalized = timeStr.trim().toLowerCase().replace(/\s+/g, '');
  const match = normalized.match(/^(\d{1,2}):(\d{2})(am|pm)$/);
  if (!match) return 0;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3];
  if (period === 'am' && hours === 12) hours = 0;
  if (period === 'pm' && hours !== 12) hours += 12;
  return hours * 60 + minutes;
}

export default FairnessReport;
